import Deal from '@r6digital/rapidstor-types/types/Deal';
import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { getNumberWithOrdinalSuffix } from '@/core/utils/phrasing';
import { formatDealCustomText } from '@/features/rapidstor/helpers';

// --- Predicates ---

export function isDealFree(deal: Deal): boolean {
    return (
        deal.discountPlan?.dcPCDiscount === 100 ||
        (deal.rates.discounted && deal.rates.discountedRate === 0)
    );
}

export function isDealPrepaid(deal: Deal): boolean {
    return (
        deal.discountPlan?.bPrepay === true ||
        (deal.discountPlan?.iPrePaidMonths ?? 0) > 1 ||
        deal.rates.prepaidDiscount === true
    );
}

export function isDealCustomHeading(deal: Deal): boolean {
    return !!(deal.customFormat?.heading && deal.customFormat.heading.trim() !== '');
}

/**
 * Returns true when the deal uses a fixed charge amount for a specific period
 * (dcChgAmt > 0). The discountedRate in these deals is a one-time special price,
 * not the ongoing monthly rate, so it must not be displayed as a per-month figure.
 */
export function isDealChargeAmount(deal: Deal): boolean {
    return (deal.discountPlan?.dcChgAmt ?? 0) > 0;
}

/**
 * Returns true only when the deal actually saves the customer money. Some `deals[]`
 * entries (e.g. a themed listing like "Wine Storage") carry `discountPlan: null` and
 * `rates.discounted: false` — those should never be presented with "valued at $X" /
 * savings-percentage discount framing.
 */
export function dealHasMonetaryDiscount(deal?: Deal | null): boolean {
    return !!deal?.rates.discounted && !!deal?.rates.discountSaved;
}

// --- Discount type ---

export type DealDiscountType = 'percentage' | 'charge' | 'fixed' | 'none';

export function getDealDiscountType(deal: Deal): DealDiscountType {
    if ((deal.discountPlan?.dcPCDiscount ?? 0) > 0) return 'percentage';
    if ((deal.discountPlan?.dcChgAmt ?? 0) > 0) return 'charge';
    if ((deal.discountPlan?.dcFixedDiscount ?? 0) > 0) return 'fixed';
    return 'none';
}

// --- Price display ---

/**
 * Returns the formatted one-off charge amount for a dcChgAmt deal, using
 * formatPriceLabel in shortForm (no recurring period suffix, since this is
 * not a monthly rate). Returns null for any other deal type.
 */
export function getDealChargeText(deal: Deal, location: Location): string | null {
    if (!isDealChargeAmount(deal)) return null;
    const rate = deal.rates.discountedRate ?? deal.rates.rate;
    return formatPriceLabel(formatCurrency(rate, location), location, false, true);
}

/**
 * Returns the default descriptive text shown below the price for a deal.
 * - Free month (100% discount): "Month X free"
 * - Charge amount (dcChgAmt): "Pay £X in your Nth month [for N months]"
 * - All other deal types: null — caller should fall back to custom format text or their own logic.
 */
export function getDealDefaultText(deal: Deal, location: Location): string | null {
    if (isDealFree(deal)) {
        const months = deal.discountPlan?.iExpirMonths;
        if (!months) return null;
        return `Month ${months} free`;
    }

    if (isDealChargeAmount(deal)) {
        const rate = deal.rates.discountedRate ?? deal.rates.rate;
        const formattedRate = formatPriceLabel(formatCurrency(rate, location), location, false, true);
        const inMonth = getNumberWithOrdinalSuffix(deal.discountPlan.iInMonth);
        const expirMonths = deal.discountPlan.iExpirMonths ?? 0;
        const monthSuffix = expirMonths > 1
            ? ` for ${getNumberWithOrdinalSuffix(expirMonths)} months`
            : '';
        return `Pay ${formattedRate} in your ${inMonth} month${monthSuffix}`;
    }

    return null;
}

/**
 * Returns the display string for a deal's price.
 * If the deal has a non-empty customFormat.heading, it is formatted via
 * formatDealCustomText and returned instead of the rate.
 * Otherwise returns the rate formatted as a price label.
 *
 * @param effectiveRate - Pre-computed effective rate (use getEffectiveDealRate from dealMatching.ts)
 */
export function getDealPriceDisplay(
    deal: Deal,
    effectiveRate: number,
    location: Location,
    shortForm?: boolean,
): string {
    if (isDealCustomHeading(deal)) {
        return formatDealCustomText(deal, location, deal.customFormat.heading) ?? '';
    }
    // Charge amount deals apply a special price for one period only — show the
    // standard ongoing rate so the user isn't misled into thinking it's recurring.
    if (isDealChargeAmount(deal)) {
        return formatPriceLabel(formatCurrency(deal.rates.rate, location), location, false, shortForm);
    }
    return formatPriceLabel(formatCurrency(effectiveRate, location), location, false, shortForm);
}

// --- Comparison type ---

export type DealComparisonType = 'same' | 'same-type' | 'different' | 'no-deals';

/**
 * Determines how two deals relate for price comparison purposes.
 *
 * - 'no-deals': neither unit has a deal — compare rates normally
 * - 'same': both use the same deal (_id match) — compare discounted rates
 * - 'same-type': different deals but same discount mechanism — compare directly
 * - 'different': incompatible deals or only one side has a deal — show full rate
 */
export function getDealComparisonType(
    dealA: Deal | undefined,
    dealB: Deal | undefined,
): DealComparisonType {
    if (!dealA && !dealB) return 'no-deals';
    if (!dealA || !dealB) return 'different';
    if (dealA._id === dealB._id) return 'same';
    const typeA = getDealDiscountType(dealA);
    const typeB = getDealDiscountType(dealB);
    if (typeA !== 'none' && typeA === typeB) return 'same-type';
    return 'different';
}

// --- Price difference display ---

export interface PriceDifferenceOptions {
    showTotal?: boolean;
    isDefault?: boolean;
}

/**
 * Returns a formatted price difference string for upgrade/alternative unit comparisons.
 *
 * Rules:
 * - isDefault or showTotal: return the full target rate
 * - 'different' comparison: return full target rate (no +/- comparison)
 * - 'same', 'same-type', 'no-deals': show +/- difference, or plain rate if equal
 *
 * Both sourceRate and targetRate should be effective rates
 * (already accounting for free deals via getEffectiveDealRate).
 */
export function getPriceDifferenceDisplay(
    sourceRate: number,
    targetRate: number,
    sourceDeal: Deal | undefined,
    targetDeal: Deal | undefined,
    location: Location,
    options?: PriceDifferenceOptions,
): string {
    // Charge amount deals apply a special price for one period only.
    // Compare against the ongoing standard rate so the difference reflects
    // the actual recurring cost, not a misleading one-time charge.
    const effectiveTargetRate = (targetDeal && isDealChargeAmount(targetDeal))
        ? targetDeal.rates.rate
        : targetRate;

    if (options?.isDefault || options?.showTotal) {
        return formatPriceLabel(formatCurrency(effectiveTargetRate, location), location);
    }

    // Charge amount deals have a one-time special price but a normal standard rate.
    // Treat them as no-deal for comparison so the ongoing rate difference shows as +/-.
    const comparison = (targetDeal && isDealChargeAmount(targetDeal))
        ? 'no-deals'
        : getDealComparisonType(sourceDeal, targetDeal);

    if (comparison === 'different') {
        return formatPriceLabel(formatCurrency(effectiveTargetRate, location), location, false, false);
    }

    const diff = effectiveTargetRate - sourceRate;

    if (diff === 0) {
        return formatPriceLabel(formatCurrency(effectiveTargetRate, location), location, false, false);
    }

    if (diff > 0) {
        return `+${formatPriceLabel(formatCurrency(diff, location), location, false, false)}`;
    }

    return `-${formatPriceLabel(formatCurrency(Math.abs(diff), location), location, false, false)}`;
}
